import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import unifiedApi from '../utils/unifiedApi';

export const DEFAULT_BRAND_NAME = 'Quail Game';
export const DEFAULT_BRAND_LOGO = '/quail_game_logo.png';

// Without a provider (e.g. tests) the QuailGame branding is used and treated as loaded.
const OrganizationBrandingContext = createContext({
  name: null,
  logoUrl: null,
  loaded: true,
  refresh: async () => {},
});

/**
 * Loads the organization's name and logo once per app load (public endpoint, works signed out).
 * Falls back to the QuailGame branding when the request fails.
 */
export const OrganizationBrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({ name: null, logoUrl: null });
  const [loaded, setLoaded] = useState(false);
  const latestRequest = useRef(0);

  const refresh = useCallback(async () => {
    // Overlapping refreshes (e.g. logo upload then save) must not let an older response win.
    const request = ++latestRequest.current;
    let next;
    try {
      const settings = await unifiedApi.getTenantSettings();
      next = {
        name: settings?.name?.trim() || null,
        logoUrl: settings?.logoUrl?.trim() || null,
      };
    } catch {
      next = { name: null, logoUrl: null };
    }
    if (request !== latestRequest.current) return;
    setBranding(next);
    setLoaded(true);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...branding, loaded, refresh }), [branding, loaded, refresh]);

  return <OrganizationBrandingContext.Provider value={value}>{children}</OrganizationBrandingContext.Provider>;
};

/** `{ name, logoUrl, loaded, refresh }` — name/logoUrl are null when the organization hasn't set them. */
export const useOrganizationBranding = () => useContext(OrganizationBrandingContext);
